import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { Card, CardHeader } from '@/components/layout/ui'
import {
  Button,
  ErrorBanner,
  LoadingPane,
  cn,
  useRoster,
  type MemberRow,
} from '@/features/issue'
import { orgProjectsApi } from '@/lib/api'
import { queryKeys } from '@/lib/queryClient'
import { roleAtLeast } from '@/lib/permissions'
import { useWorkspace } from '@/lib/workspace'

import { ColumnsCard } from './ColumnsCard'
import { DangerZone } from './DangerZone'
import { GeneralCard } from './GeneralCard'
import { useColumnsQuery } from './useColumns'

type Tab = 'general' | 'columns'

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'general', label: 'General' },
  { id: 'columns', label: 'Columns' },
]

/**
 * `/{slug}/projects/{key}/settings` — the screen the board's gear opens.
 *
 * Two sections, because they answer two different questions. GENERAL is what
 * the project is: name, description, lead, whether it is still in circulation.
 * COLUMNS is how the board works, and it is the reason this screen exists — the
 * data model has supported configurable columns all along and nothing exposed
 * it, so a board was permanently To Do / In Progress / Done.
 *
 * The tab lives in the query string rather than in state so a link can point
 * straight at the columns, which is where anyone sent here from the board wants
 * to land.
 */
export default function ProjectSettingsPage() {
  const workspace = useWorkspace()
  const queryClient = useQueryClient()
  const { projectKey = '' } = useParams<{ projectKey: string }>()
  const [params, setParams] = useSearchParams()
  // General is the default, not Columns. It is first in the tab list and it
  // holds the project's identity — name, key, icon, colour — so it is what
  // "project settings" means to someone who came here to rename something.
  // Defaulting to Columns meant every entry point landed on the one tab
  // WITHOUT the rename field, which read as the rename not existing.
  const tab: Tab = params.get('tab') === 'columns' ? 'columns' : 'general'

  const roster = useRoster(workspace.slug)
  const canEdit = roster.role !== null && roleAtLeast(roster.role, 'admin')

  const project = useQuery({
    queryKey: [...queryKeys.orgProjects(workspace.slug), 'one', projectKey] as const,
    queryFn: ({ signal }) => orgProjectsApi.get(workspace.slug, projectKey, { signal }),
    enabled: projectKey.length > 0,
  })

  const columns = useColumnsQuery(workspace.slug, projectKey || null)

  const save = useMutation({
    mutationFn: (input: Parameters<typeof orgProjectsApi.update>[2]) =>
      orgProjectsApi.update(workspace.slug, projectKey, input),
    onSuccess: async () => {
      // Three caches show a project: this screen, the shell's sidebar list, and
      // the board/backlog copy. A rename that only lands in one of them reads
      // as a bug.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.orgProjects(workspace.slug) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.projects }),
        queryClient.invalidateQueries({ queryKey: ['v2', 'projects', 'legacy'] }),
      ])
    },
  })

  const boardPath = workspace.path(`/projects/${encodeURIComponent(projectKey)}/board`)

  if (project.isError) {
    return (
      <div className="mx-auto max-w-4xl px-6 py-10">
        <ErrorBanner error={project.error} onRetry={() => void project.refetch()} />
        <Link to={boardPath} className="mt-4 inline-block text-sm font-medium text-brand-700 hover:underline">
          Back to the board
        </Link>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-10">
      <header className="mb-6">
        <Link
          to={boardPath}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-500 transition hover:text-ink-900"
        >
          <svg viewBox="0 0 16 16" className="size-3.5" fill="none" aria-hidden="true">
            <path d="M9.5 3.5 5 8l4.5 4.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to the board
        </Link>
        <h1 className="mt-2 text-xl font-semibold tracking-tight text-ink-900">
          {project.data?.name ?? projectKey} settings
        </h1>
        <p className="mt-1 text-sm text-ink-500">
          {project.data?.project_key ? (
            <>
              Issues in this project are keyed{' '}
              <span className="font-mono">{project.data.project_key}-1</span>,{' '}
              <span className="font-mono">{project.data.project_key}-2</span>, and so on.
            </>
          ) : (
            'Project configuration.'
          )}
        </p>
      </header>

      <div className="mb-8 border-b border-ink-200">
        <nav aria-label="Project settings" className="-mb-px flex gap-1">
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              aria-current={tab === entry.id ? 'page' : undefined}
              onClick={() =>
                setParams(
                  (current) => {
                    const next = new URLSearchParams(current)
                    next.set('tab', entry.id)
                    return next
                  },
                  { replace: true },
                )
              }
              className={cn(
                'border-b-2 px-3 py-2 text-sm whitespace-nowrap transition',
                tab === entry.id
                  ? 'border-brand-600 font-medium text-brand-700'
                  : 'border-transparent text-ink-500 hover:text-ink-900',
              )}
            >
              {entry.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'general' ? (
        project.isPending ? (
          <LoadingPane label="Loading project" />
        ) : (
          <div className="space-y-6">
            <GeneralCard
              project={project.data}
              members={roster.members as MemberRow[]}
              canEdit={canEdit}
              pending={save.isPending}
              error={save.error}
              saved={save.isSuccess}
              onSave={(input) => save.mutate(input)}
            />
            {/* Moving the default is what UNBLOCKS the two cards below it. A
                workspace keeps exactly one default project, and both archiving
                and deleting refuse it — so without this, the first project a
                workspace ever created could never be removed, and the Archive
                button simply appeared to be missing. */}
            {canEdit && !project.data.is_default ? (
              <Card>
                <CardHeader
                  title="Make this the default project"
                  description="The default is where issues from deleted projects go, and what the iPad opens when nothing is selected."
                />
                <div className="p-5">
                  <Button
                    disabled={save.isPending || Boolean(project.data.archived_at)}
                    onClick={() => save.mutate({ is_default: true })}
                  >
                    Make default
                  </Button>
                  {project.data.archived_at ? (
                    <p className="mt-2 text-sm text-ink-500">
                      Restore this project first — an archived project cannot be the default.
                    </p>
                  ) : null}
                </div>
              </Card>
            ) : null}
            {canEdit ? (
              <ArchiveCard
                archived={Boolean(project.data.archived_at)}
                isDefault={Boolean(project.data.is_default)}
                pending={save.isPending}
                onToggle={(archived) => save.mutate({ archived })}
              />
            ) : null}
            {/* Last, and visually separated: deleting is the one action here
                that destroys data, and archiving above is the reversible answer
                to the same problem. Somebody who wanted to tidy up should meet
                that first. */}
            {canEdit ? <DangerZone project={project.data} canEdit={canEdit} /> : null}
          </div>
        )
      ) : columns.isPending ? (
        <LoadingPane label="Loading columns" />
      ) : columns.isError ? (
        <ErrorBanner error={columns.error} onRetry={() => void columns.refetch()} />
      ) : (
        <ColumnsCard
          slug={workspace.slug}
          projectKey={projectKey}
          columns={columns.data}
          canEdit={canEdit}
          upgradeHref={workspace.can('org.billing') ? workspace.path('/settings/billing') : null}
        />
      )}
    </div>
  )
}

/**
 * Archiving takes a project out of the switcher without deleting anything.
 *
 * The default project cannot be archived — every workspace keeps exactly one,
 * because the iPad falls back to it when nothing is selected — so the control
 * says so rather than offering a button the server answers 422 to.
 */
function ArchiveCard({
  archived,
  isDefault,
  pending,
  onToggle,
}: {
  archived: boolean
  isDefault: boolean
  pending: boolean
  onToggle: (archived: boolean) => void
}) {
  const [confirming, setConfirming] = useState(false)

  return (
    <section className="rounded-card border border-ink-200 bg-white">
      <header className="border-b border-ink-200 px-5 py-4">
        <h2 className="text-sm font-semibold tracking-tight text-ink-900">
          {archived ? 'Restore' : 'Archive'}
        </h2>
        <p className="mt-0.5 text-sm text-ink-500">
          {archived
            ? 'Bring this project back into the switcher.'
            : 'Hide it from the switcher. Nothing is deleted and every link keeps working.'}
        </p>
      </header>
      <div className="flex flex-wrap items-center gap-3 p-5">
        {isDefault && !archived ? (
          <p className="text-sm text-ink-500">
            This is the workspace’s default project. It cannot be archived — the iPad app falls
            back to it when nothing is selected. Open another project’s settings and make that one
            the default first.
          </p>
        ) : archived ? (
          <Button variant="primary" disabled={pending} onClick={() => onToggle(false)}>
            Restore project
          </Button>
        ) : confirming ? (
          <>
            <span className="text-sm text-ink-700">Archive this project?</span>
            <Button variant="danger" disabled={pending} onClick={() => onToggle(true)}>
              Yes, archive
            </Button>
            <Button disabled={pending} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button variant="danger" onClick={() => setConfirming(true)}>
            Archive project
          </Button>
        )}
      </div>
    </section>
  )
}
